import { useState, useCallback } from "react";
import { Mic, AlertCircle, CheckCircle2, Save, X } from "lucide-react";
import type { InventoryRow } from "../types";
import { currentMonth, appendToLedger } from "../services/inventory";
import { transcribeAudio, structureWithClaude } from "../services/openrouter";
import VoiceRecorder from "../components/VoiceRecorder";
import PreviewTable from "../components/PreviewTable";

type Phase = "idle" | "recording" | "transcribing" | "structuring" | "review" | "saving";

const PHASE_LABELS: Record<Phase, string> = {
  idle: "Idle",
  recording: "Recording...",
  transcribing: "Transcribing audio (Whisper)...",
  structuring: "Structuring data (Claude)...",
  review: "Review",
  saving: "Saving to ledger...",
};

export default function VoiceImport() {
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [previewRows, setPreviewRows] = useState<InventoryRow[]>([]);
  const [transcript, setTranscript] = useState("");

  const handleRecordingComplete = useCallback(async (base64: string, mimeType: string) => {
    setError(null);
    setPhase("transcribing");
    try {
      const text = await transcribeAudio(base64, mimeType);
      setTranscript(text);
      setPhase("structuring");
      const result = await structureWithClaude(text);
      const items = result.items.map((i) => ({
        item: i.item,
        quantity: i.quantity,
        unit: i.unit,
        unit_price: i.unit_price,
        total: i.quantity * i.unit_price,
      }));
      setPreviewRows(items);
      setPhase("review");
    } catch (e: any) {
      setError(e?.message || "Processing failed");
      setPhase("idle");
    }
  }, []);

  const handleSave = async () => {
    if (previewRows.length === 0) return;
    setPhase("saving");
    setError(null);
    try {
      const month = currentMonth();
      await appendToLedger(month, "raw_materials", previewRows);
      setSuccess(`${previewRows.length} row${previewRows.length !== 1 ? "s" : ""} saved to ${month}`);
      setPhase("idle");
    } catch (e: any) {
      setError(e?.message || "Save failed");
      setPhase("review");
    }
  };

  const discard = () => {
    setPreviewRows([]);
    setTranscript("");
    setError(null);
    setPhase("idle");
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-citilux-blue-600/20 flex items-center justify-center">
          <Mic className="w-5 h-5 text-citilux-blue-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Voice Import</h1>
          <p className="text-sm text-gray-500 dark:text-metallic-silver/60">
            {PHASE_LABELS[phase]}
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 bg-red-100 dark:bg-red-500/10 border border-red-300 dark:border-red-500/20 text-red-700 dark:text-red-400 text-sm rounded-xl px-4 py-3">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-500/60 hover:text-red-700 dark:hover:text-red-400">✕</button>
        </div>
      )}

      {/* Success */}
      {success && (
        <div className="flex items-center gap-2 bg-green-100 dark:bg-green-500/10 border border-green-300 dark:border-green-500/20 text-green-700 dark:text-green-400 text-sm rounded-xl px-4 py-3">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          {success}
          <button onClick={() => setSuccess(null)} className="ml-auto text-green-500/60 hover:text-green-700 dark:hover:text-green-400">✕</button>
        </div>
      )}

      {/* Phase indicator */}
      {(phase === "transcribing" || phase === "structuring") && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-citilux-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="mt-4 text-sm text-gray-500 dark:text-metallic-silver/70">{PHASE_LABELS[phase]}</p>
        </div>
      )}

      {/* Voice recorder (idle / recording) */}
      {(phase === "idle" || phase === "recording") && (
        <div className="flex flex-col items-center py-12">
          <VoiceRecorder onRecordingComplete={handleRecordingComplete} disabled={phase !== "idle"} />
        </div>
      )}

      {/* Transcript */}
      {transcript && phase === "review" && (
        <div className="bg-gray-50 dark:bg-navy-900 border border-light-border dark:border-navy-700/40 rounded-2xl p-4">
          <p className="text-xs uppercase tracking-widest text-gray-500 dark:text-metallic-silver/60 mb-1">Transcript</p>
          <p className="text-sm text-gray-700 dark:text-white/80 italic">{transcript}</p>
        </div>
      )}

      {/* Preview table (review phase) */}
      {phase === "review" && (
        <div className="space-y-4">
          <PreviewTable rows={previewRows} onChange={setPreviewRows} />

          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={previewRows.length === 0}
              className="flex items-center gap-2 bg-citilux-blue-600 hover:bg-citilux-blue-500 disabled:bg-gray-400 dark:disabled:bg-navy-700 text-sm font-semibold text-white rounded-xl px-5 py-2.5 transition-colors"
            >
              <Save className="w-4 h-4" /> Save to Ledger
            </button>
            <button
              onClick={discard}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-light-border dark:border-navy-700 text-sm text-gray-600 dark:text-metallic-silver hover:bg-gray-100 dark:hover:bg-navy-700 transition-colors"
            >
              <X className="w-4 h-4" /> Discard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
